import { Component, OnInit, OnDestroy, HostBinding, HostListener, Inject, PLATFORM_ID, ElementRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { navigate, openWindow } from '../../../../actions/core.actions';
import { RoutingPathResolver } from '../../../../app-routing-path-resolver';
import { Store } from '@ngrx/store';
import { filter, shareReplay } from 'rxjs/operators';
import { RootState } from '../../../../reducers';
import { getCommonIdSignedInUser } from '../../../../selectors/common-id/common-id-auth.selectors';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { CommonIdUser } from '../../../../models/common-id/common-id-user';
import { NavigationEnd, Router } from '@angular/router';
import * as CommonIdInformationSelectors from '../../../../selectors/common-id/common-id-information.selectors';
import { CommonIdUserUtil } from '../../../../utils/common-id/common-id-user-util';
import { CurrentDateTime } from '../../../../models/current-date-time';
import { getCurrentDateTime } from '../../../../actions/current-date-time.actions';
import { getCurrentDateTime as getCurrentDateTimeSelector } from '../../../../selectors/current-date-time.selectors';
import { environment } from '../../../../../environments/environment';
import { CommonIdAuthService } from '../../../../services/common-id/common-id-auth.service';

@Component({
  selector: 'app-common-id-sign-in-header',
  templateUrl: './sign-in-header.component.html',
  styleUrls: ['./sign-in-header.component.scss']
})
export class CommonIdSignInHeaderComponent implements OnInit, OnDestroy {
  user$: Observable<CommonIdUser>;
  currentDateTime$: Observable<CurrentDateTime>;
  newInformation$: Observable<boolean>;
  isPremium$: Observable<boolean>;
  nickname$: Observable<string>;
  currentUrl: string;
  userInfoParentIsFocused = false;
  userInfoItemsIsFocused: boolean[] = [];

  private signedInUserIdSubscription: Subscription;

  @HostBinding('class.header-hidden') headerHidden = false;

  private lastScrollY = 0;
  /** 下方向へのスクロール開始地点 */
  private downStartY = 0;
  private wasScrollingDown = false;
  private isBrowser: boolean;

  constructor(
    private store: Store<RootState>,
    private router: Router,
    private elementRef: ElementRef,
    private commonIdAuthService: CommonIdAuthService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (!this.isBrowser) return;

    const y = window.scrollY;

    if (y <= 0) {
      this.setHeaderHidden(false);
      this.lastScrollY = 0;
      this.downStartY = 0;
      return;
    }

    if (y === this.lastScrollY) return;

    const isDown = y > this.lastScrollY;

    if (isDown) {
      if (!this.wasScrollingDown) {
        this.downStartY = this.lastScrollY;
        this.wasScrollingDown = true;
      }
      const hideThreshold = this.elementRef.nativeElement.offsetHeight;
      if (y - this.downStartY > hideThreshold && !this.showUserInfoDropdown()) {
        this.setHeaderHidden(true);
      }
    } else {
      this.wasScrollingDown = false;
      this.setHeaderHidden(false);
    }

    this.lastScrollY = y;
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateHeaderOffset();
  }

  ngOnInit() {
    this.updateHeaderOffset();
    this.setUpUser();
    this.currentUrl = this.router.url;
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.currentUrl = event.url;
    });
    this.newInformation$ = this.store.select(CommonIdInformationSelectors.getNewInformation).pipe(
      filter(it => it != null),
      shareReplay(1)
    );

    this.signedInUserIdSubscription = this.commonIdAuthService.signedInUserId$.subscribe(userId => {
      if (!userId.includes('common-id')) {
        this.store.dispatch(openWindow({ url: RoutingPathResolver.resolveCommonIdSignOut(), target: '_self' }));
      }
    });
  }

  ngOnDestroy(): void {
    if (this.signedInUserIdSubscription) {
      this.signedInUserIdSubscription.unsubscribe();
    }
    if (this.isBrowser) {
      document.documentElement.style.removeProperty('--header-height');
      document.documentElement.style.removeProperty('--header-shift');
      document.documentElement.style.removeProperty('--header-shift-duration');
    }
  }

  touchendUserInfoDropdown() {
    if (this.userInfoParentIsFocused) {
      setTimeout(() => {
        // 遅延させないと遷移が発生しなくなる
        this.resetUserInfoDropdown();
      }, 10);
    } else {
      this.userInfoParentIsFocused = true;
    }
  }

  hoverUserInfoDropdown() {
    this.userInfoParentIsFocused = true;
  }

  focusUserInfoParent() {
    this.userInfoParentIsFocused = true;
  }

  blurUserInfoParent() {
    this.userInfoParentIsFocused = false;
  }

  focusUserInfoItem(index: number) {
    this.userInfoItemsIsFocused[index] = true;
  }

  blurUserInfoItem(index: number) {
    this.userInfoItemsIsFocused[index] = false;
  }

  showUserInfoDropdown(): boolean {
    function checkItem(val: boolean): boolean {
      return val;
    }
    return this.userInfoParentIsFocused || this.userInfoItemsIsFocused.some(b => checkItem(b));
  }

  resetUserInfoDropdown() {
    this.userInfoParentIsFocused = false;
    this.userInfoItemsIsFocused = [];
  }

  goHome(event: Event) {
    event.preventDefault();
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdHome() }));
  }

  showSearch() {
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdSearchUniv() }));
  }

  showSearchByCategories() {
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdSearchByCategories() }));
  }

  showPlaylists() {
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdPlaylists() }));
  }

  showMylist() {
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdMylistAnswerProblems() }));
  }

  showHelp() {
    this.resetUserInfoDropdown();
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdHelp() }));
  }

  showInformation() {
    this.resetUserInfoDropdown();
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdInformation() }));
  }

  showPurchase() {
    this.resetUserInfoDropdown();
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdPurchase() }));
  }

  showPayments() {
    this.resetUserInfoDropdown();
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdPayments() }));
  }

  gotoProfile() {
    this.resetUserInfoDropdown();
    this.store.dispatch(openWindow({ url: environment.commonIdConfig.profileUrl }));
  }

  /**
   * ホーム⇔タブ間インクバーアニメーション用。
   * ホーム画面にはナビタブが存在しないため、非表示の幅0ホームアンカーを
   * active にしてインクバーをDOM上に維持し、遷移アニメーションを繋げる。
   * @see sign-in-header.component.html 内の .sign-in-gnav-home-anchor
   */
  isCurrentHome(): boolean {
    return this.currentUrl.includes(RoutingPathResolver.resolveCommonIdHome());
  }

  isCurrentSearch(): boolean {
    return this.currentUrl.includes(RoutingPathResolver.resolveCommonIdSearchUniv());
  }

  isCurrentSearchByCategories(): boolean {
    return this.currentUrl.includes(RoutingPathResolver.resolveCommonIdSearchByCategories());
  }

  isCurrentPlaylists(): boolean {
    return this.currentUrl.includes(RoutingPathResolver.resolveCommonIdPlaylists());
  }

  isCurrentMylist(): boolean {
    return this.currentUrl.includes(RoutingPathResolver.resolveCommonIdMylist());
  }

  signOut() {
    this.resetUserInfoDropdown();
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdSignOut() }));
  }

  private setHeaderHidden(hidden: boolean) {
    if (this.headerHidden === hidden) return;
    this.headerHidden = hidden;
    this.updateHeaderOffset();
  }

  private updateHeaderOffset() {
    if (!this.isBrowser) return;
    const height = this.elementRef.nativeElement.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${height}px`);
    document.documentElement.style.setProperty('--header-shift', this.headerHidden ? `${-height}px` : '0px');
    document.documentElement.style.setProperty('--header-shift-duration', this.headerHidden ? '0s' : '0.4s');
  }

  private setUpUser() {
    this.store.dispatch(getCurrentDateTime());
    const currentDateTime$ = this.store.select(getCurrentDateTimeSelector).pipe(
      filter(it => it != null),
      shareReplay(1)
    );

    const signedInUser$ = this.store.select(getCommonIdSignedInUser).pipe(
      filter<CommonIdUser>(it => it != null && it !== 'none'),
      shareReplay(1)
    );

    combineLatest([currentDateTime$, signedInUser$]).subscribe(([currentDateTime, signedInUser]) => {
      this.nickname$ = of(signedInUser.nickname);
      this.isPremium$ = of(CommonIdUserUtil.getIsPremiumUser(currentDateTime, signedInUser));
    });
  }
}
